/* Live public lineups backed by private, unpurchasable inventory cards. */
(() => {
  const strings = JSON.parse(document.getElementById('PackPreviewStrings').textContent);
  const cache = new Map();
  const initialized = new WeakSet();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const formatMoney = (cents, currency = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100);
  const ranks = ['common', 'uncommon', 'rare', 'rare holo', 'holo rare', 'double rare', 'ultra rare', 'illustration rare', 'special illustration rare', 'hyper rare', 'secret rare'];
  const rarityRank = (rarity) => ranks.indexOf(rarity.toLowerCase().trim().replace(/[_-]+/g, ' '));
  const loadCards = (path, order, gallery = false) => {
    const key = `${path}:${order}:${gallery ? 'gallery' : 'available'}`;
    if (cache.has(key)) return cache.get(key);
    const promise = (async () => {
      const tier = new URL(path, location.origin).pathname.split('/').filter(Boolean).pop();
      if (!['gold', 'silver', 'bronze'].includes(tier)) throw new Error('Invalid pack tier');
      const response = await fetch(`/apps/packs/lineup?tier=${encodeURIComponent(tier)}${gallery ? '&view=gallery' : ''}`, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Lineup unavailable');
      const payload = await response.json();
      if (payload.tier !== tier || !Array.isArray(payload.cards)) throw new Error('Invalid lineup');
      const cutouts = JSON.parse(document.getElementById('PackCardCutouts')?.textContent || '{}');
      const hasGallery = gallery && Array.isArray(payload.gallery);
      const result = (hasGallery ? payload.gallery : payload.cards).map(card => {
        const cutout = cutouts[card.id];
        const image = /^https:\/\//.test(card.cutout || '') ? card.cutout :
          cutout && card.image.includes(cutout.source) ? cutout.url : card.image;
        const imageURL = new URL(image, location.origin);
        imageURL.searchParams.set('width', '700');
        const status = hasGallery ? (['available', 'pulled', 'unavailable'].includes(card.status) ? card.status : 'unavailable') : 'available';
        return { ...card, image: imageURL.href, price: Number(card.price), status };
      });
      if (order === 'value') result.sort((a, b) => b.price - a.price || rarityRank(b.rarity) - rarityRank(a.rarity));
      else if (order !== 'collection') result.sort((a, b) => rarityRank(b.rarity) - rarityRank(a.rarity));
      const tiers = { grail: 0, chase: 1, lineup: 2 };
      if (order !== 'value') result.sort((a, b) => (tiers[a.tier] ?? 2) - (tiers[b.tier] ?? 2));
      return result;
    })().catch((error) => { cache.delete(key); throw error; });
    cache.set(key, promise);
    return promise;
  };

  const initHero = async (host) => {
    try {
      const cards = (await loadCards(host.dataset.packUrl, 'value')).slice(0, 3);
      if (!cards.length) return;
      const positions = cards.length === 1 ? ['center'] : cards.length === 2 ? ['left', 'right'] : ['left', 'center', 'right'];
      // The best card takes the center, with the next two on either side.
      const featured = cards.length === 3 ? [cards[1], cards[0], cards[2]] : cards;
      const images = featured.map((card, i) => {
        const image = new Image(); image.src = card.image; image.alt = '';
        image.className = 'hero-card-photo';
        image.width = 700; image.height = 1100;
        return image;
      });
      // A slow or unavailable photograph leaves the original pack in place.
      await Promise.all(images.map(image => image.decode()));
      if (!host.isConnected) return;
      const frames = images.map((image, i) => {
        const frame = document.createElement('span'); frame.className = 'hero-featured-card';
        frame.dataset.position = positions[i]; frame.dataset.cardId = featured[i].id; frame.dataset.value = String(featured[i].price);
        frame.style.setProperty('--card-ratio', image.naturalWidth / image.naturalHeight);
        frame.style.setProperty('--card-image', `url("${image.src}")`);
        const turn = document.createElement('span'); turn.className = 'hero-card-turn';
        const front = document.createElement('span'); front.className = 'hero-card-front';
        const shimmer = document.createElement('span'); shimmer.className = 'hero-card-shimmer';
        const glint = document.createElement('span'); glint.className = 'hero-card-glint';
        const back = host.querySelector('[data-hero-card-back]').content.firstElementChild.cloneNode(true);
        const float = document.createElement('span'); float.className = 'hero-card-float';
        const touch = document.createElement('span'); touch.className = 'hero-card-touch';
        front.append(image, shimmer, glint); turn.append(front, back); touch.append(turn); float.append(touch); frame.append(float);
        let pointerFrame = 0, hovering = false;
        const reset = () => {
          cancelAnimationFrame(pointerFrame); pointerFrame = 0;
          frame.removeAttribute('data-card-hover'); frame.style.removeProperty('z-index');
          for (const property of ['--card-tilt-x','--card-tilt-y','--glint-x','--glint-y']) frame.style.removeProperty(property);
          if (hovering) host.dispatchEvent(new CustomEvent('piranha:hero:hover',{bubbles:true,detail:{active:false}}));
          hovering = false;
        };
        frame.addEventListener('pointermove', event => {
          if (event.pointerType !== 'mouse' || reducedMotion.matches || !host.closest('[data-hero-slide]').hasAttribute('data-active')) return;
          if (turn.getAnimations().some(animation => animation.playState !== 'finished')) return;
          cancelAnimationFrame(pointerFrame);
          const {clientX,clientY} = event;
          pointerFrame = requestAnimationFrame(() => {
            const rect = frame.getBoundingClientRect();
            const x = Math.max(-1,Math.min(1,(clientX-rect.left)/rect.width*2-1));
            const y = Math.max(-1,Math.min(1,(clientY-rect.top)/rect.height*2-1));
            frame.style.setProperty('--card-tilt-x',`${-y*5}deg`); frame.style.setProperty('--card-tilt-y',`${x*7}deg`);
            frame.style.setProperty('--glint-x',`${(x+1)*50}%`); frame.style.setProperty('--glint-y',`${(y+1)*50}%`);
            frame.dataset.cardHover = ''; frame.style.zIndex = '5';
            if (!hovering) host.dispatchEvent(new CustomEvent('piranha:hero:hover',{bubbles:true,detail:{active:true}}));
            hovering = true;
          });
        });
        frame.addEventListener('pointerleave',reset);
        frame.addEventListener('pointercancel',reset);
        host.closest('[data-hero-slide]').addEventListener('piranha:hero:reset',reset);
        frame.addEventListener('animationstart',event => { if(event.animationName === 'hero-card-flip') reset(); });
        return frame;
      });
      const pack = host.querySelector('.hero-pack--front');
      const wrapper = document.createElement('span'); wrapper.className = 'hero-rip-pack'; wrapper.setAttribute('aria-hidden','true');
      for (const half of ['top', 'bottom']) {
        const layer = document.createElement('span'); layer.className = `hero-rip-${half}`;
        const image = pack.cloneNode(); image.className = 'hero-rip-image'; image.removeAttribute('loading');
        layer.append(image); wrapper.append(layer);
      }
      const seam = document.createElement('span'); seam.className = 'hero-rip-seam'; wrapper.append(seam);
      host.append(wrapper);
      host.querySelector('[data-hero-card-fan]').replaceChildren(...frames);
      const motes = document.createElement('span'); motes.className = 'hero-card-motes'; motes.setAttribute('aria-hidden','true');
      for (let i = 0; i < 9; i++) { const dot = document.createElement('i'); dot.style.setProperty('--mote-i',i); dot.style.left = `${14 + (i * 31 % 75)}%`; motes.append(dot); }
      host.append(motes);
      host.dataset.fanReady = '';
      host.dispatchEvent(new CustomEvent('piranha:hero:ready', {bubbles:true}));
    } catch { /* The native pack link remains usable without the enhancement. */ }
  };

  const initPreview = (host) => {
    const image = host.querySelector('[data-preview-image]');
    if (!image) return;
    const buffer = image.cloneNode();
    buffer.removeAttribute('data-preview-image'); buffer.setAttribute('aria-hidden', 'true');
    image.after(buffer);
    const images = [image, buffer];
    const valueBadge = host.querySelector('[data-preview-value]');
    let cards, index = 0, timer, hoverDelay, generation = 0, renderVersion = 0, paused = false, currentImage;
    const stopTimer = () => { clearInterval(timer); timer = null; };
    const show = async () => {
      const request = ++renderVersion;
      const card = cards[index];
      const incoming = currentImage === image ? buffer : image;
      // Decode offscreen, then swap directly with no movement or fade.
      incoming.src = card.image; incoming.alt = card.title;
      try { await incoming.decode(); } catch { return; }
      if (request !== renderVersion || !host.isConnected) return;
      images.forEach((item) => { item.hidden = item !== incoming; item.setAttribute('aria-hidden', String(item !== incoming)); });
      incoming.removeAttribute('aria-hidden');
      currentImage = incoming;
      if (valueBadge) {
        const known = Number.isSafeInteger(card.price) && card.price > 0 && card.currency === 'USD';
        valueBadge.hidden = !known;
        valueBadge.textContent = known ? `Est. ${new Intl.NumberFormat('en-US', {style:'currency',currency:'USD',minimumFractionDigits:card.price % 100 ? 2 : 0,maximumFractionDigits:2}).format(card.price / 100)}` : '';
      }
      host.dataset.previewActive = ''; host.dataset.previewIndex = String(index);
      if (cards.length > 1) { const upcoming = new Image(); upcoming.src = cards[(index + 1) % cards.length].image; }
    };
    const play = () => {
      stopTimer();
      if (!reducedMotion.matches && !paused && cards.length > 1) timer = setInterval(() => {
        if (!host.isConnected || document.hidden) { stopTimer(); return; }
        index = (index + 1) % cards.length; show();
      }, 1100);
    };
    const reset = () => {
      generation++; renderVersion++; clearTimeout(hoverDelay); stopTimer();
      index = 0; paused = false; currentImage = null;
      images.forEach((item) => { item.hidden = true; item.setAttribute('aria-hidden', 'true'); });
      delete host.dataset.previewActive;
      if (valueBadge) { valueBadge.hidden = true; valueBadge.textContent = ''; }
    };
    const start = async (request) => {
      try {
        cards = cards || await loadCards(host.dataset.packUrl, 'value');
        if (request !== generation || !host.isConnected || !cards.length) return;
        host.dataset.previewCount = String(cards.length); show(); play();
      } catch { if (request === generation) reset(); }
    };
    const queueStart = () => {
      clearTimeout(hoverDelay);
      const request = ++generation;
      // Fetch during the short hover-intent delay, not after it.
      if (!cards) loadCards(host.dataset.packUrl, 'value').then(loaded => {
        cards = loaded;
        if (cards[0]) { const first = new Image(); first.src = cards[0].image; }
      }).catch(() => {});
      hoverDelay = setTimeout(() => start(request), 220);
    };
    host.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') queueStart(); });
    host.addEventListener('pointerleave', reset);
    host.addEventListener('focusin', (event) => { if (!host.contains(event.relatedTarget)) queueStart(); });
    host.addEventListener('focusout', (event) => { if (!host.contains(event.relatedTarget)) reset(); });
    host.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') reset();
      if (event.code === 'Space' && cards?.length) { event.preventDefault(); paused = !paused; play(); }
      if (['ArrowRight', 'ArrowLeft'].includes(event.key) && cards?.length) {
        event.preventDefault(); paused = true; stopTimer();
        index = (index + (event.key === 'ArrowRight' ? 1 : -1) + cards.length) % cards.length; show();
      }
    });
    document.addEventListener('visibilitychange', () => { if (document.hidden) reset(); });
    reducedMotion.addEventListener('change', () => { if (host.hasAttribute('data-preview-active')) { paused = true; play(); } });
  };

  const initArt = async (host) => {
    if (!host.dataset.packUrl) return;
    try {
      const cards = await loadCards(host.dataset.packUrl, host.dataset.previewOrder, true);
      if (!cards.length || !host.isConnected) return;
      const composition = host.querySelector('[data-pack-composition]');
      const packImage = composition.querySelector('.pack-art-front');
      if (!packImage) return;
      const controls = host.querySelector('[data-art-controls]');
      const dots = host.querySelector('[data-art-dots]');
      const status = host.querySelector('[data-art-status]');
      // Include every Grail, then one Lineup card and enough other cards to fill the fan.
      const featured = cards.filter((card) => card.tier === 'grail');
      const lineup = cards.find((card) => card.tier === 'lineup');
      if (lineup) featured.push(lineup);
      for (const card of cards) { if (featured.length >= 4) break; if (!featured.includes(card)) featured.push(card); }
      const cardImages = featured.map((card) => {
        const image = new Image(); image.src = card.image; image.alt = card.title;
        image.className = 'pack-stack-item'; image.width = 700; image.height = 950;
        return image;
      });
      await Promise.all(cardImages.map((image) => image.decode().catch(() => {})));
      if (!host.isConnected) return;
      const items = [packImage, ...cardImages];
      const labels = [strings.artwork, ...featured.map((card) => card.title)];
      packImage.className = 'pack-stack-item';
      composition.replaceChildren(...items); composition.classList.add('pack-stack');
      const badges = host.querySelector('[data-art-badges]');
      const arrows = [...host.querySelectorAll('[data-art-step]')];
      // A true depth orbit keeps the return path behind the visible cards.
      // One shared animation clock and a short input lock prevent interrupted stacks.
      const slotAt = (item, active) => {
        let distance = (item - active + items.length) % items.length;
        if (distance > items.length / 2) distance -= items.length;
        return distance;
      };
      const transformAt = (slot, returning = false) => {
        const outer = innerWidth <= 749 ? 77 : 84;
        const x = slot === 0 ? 0 : Math.sign(slot) * (Math.abs(slot) === 1 ? 45 : outer);
        const scale = slot === 0 ? 1 : Math.abs(slot) === 1 ? .8 : .65;
        const z = returning ? -150 : slot === 0 ? 0 : Math.abs(slot) === 1 ? -30 : -75;
        return `translate(-50%, -50%) translate3d(${returning ? 0 : x}%, ${returning ? 3 : 0}%, ${z}px) scale(${returning ? .56 : scale})`;
      };
      // Explicit layers also keep the active image in front when a browser flattens 3D depth.
      const layerAt = (slot, returning = false) => String(returning ? 0 : Math.max(0, 3 - Math.abs(slot)));
      let index = 0, moving = false;
      const updateCaption = () => {
        const card = featured[index - 1]; badges.replaceChildren();
        if (card) {
          if (card.tier !== 'lineup') { const badge = document.createElement('span'); badge.className = `art-tier art-tier--${card.tier}`; badge.textContent = strings[card.tier] || card.tier; badges.append(badge); }
          if (Number.isSafeInteger(card.price) && card.price > 0 && /^[A-Z]{3}$/.test(card.currency)) {
            const value = document.createElement('span'); value.className = 'art-value';
            value.textContent = `Est. ${new Intl.NumberFormat('en-US', { style: 'currency', currency: card.currency, minimumFractionDigits: card.price % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(card.price / 100)}`;
            badges.append(value);
          }
        }
        if (!card) {
          const gallery = document.createElement('span'); gallery.className = 'art-rate';
          gallery.textContent = 'Batch gallery'; badges.append(gallery);
        }
        dots.querySelectorAll('button').forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
        host.dataset.artIndex = String(index); status.textContent = labels[index];
      };
      const show = async (next, animate = true) => {
        if (moving) return;
        const target = (next + items.length) % items.length;
        const previous = index; index = target;
        const shouldAnimate = animate && previous !== target && !reducedMotion.matches;
        moving = shouldAnimate;
        arrows.forEach((button) => { button.disabled = moving; });
        dots.querySelectorAll('button').forEach((button) => { button.disabled = moving; });
        const clock = document.timeline.currentTime;
        const animations = items.map((image, i) => {
          const before = slotAt(i, previous), after = slotAt(i, target);
          const visible = Math.abs(after) <= 2;
          const wasVisible = Math.abs(before) <= 2;
          image.style.visibility = visible || (shouldAnimate && wasVisible) ? 'visible' : 'hidden';
          image.dataset.stackSlot = String(after);
          image.setAttribute('aria-hidden', String(i !== index));
          const end = transformAt(after);
          image.style.transform = end;
          image.style.zIndex = layerAt(after);
          if (!shouldAnimate || (!visible && !wasVisible)) return null;
          const frames = [{ transform: transformAt(before), zIndex: layerAt(before) }];
          if (before * after < 0 && Math.abs(before) + Math.abs(after) >= 3) frames.push({ transform: transformAt(0, true), zIndex: layerAt(0, true), offset: .5 });
          frames.push({ transform: end, zIndex: layerAt(after) });
          const animation = image.animate(frames, { duration: 480, easing: 'cubic-bezier(.32,0,.2,1)' });
          animation.startTime = clock;
          return animation;
        }).filter(Boolean);
        if (shouldAnimate) await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));
        if (!host.isConnected) return;
        items.forEach((image, i) => { image.style.visibility = Math.abs(slotAt(i, index)) <= 2 ? 'visible' : 'hidden'; });
        moving = false; arrows.forEach((button) => { button.disabled = false; });
        dots.querySelectorAll('button').forEach((button) => { button.disabled = false; });
        updateCaption();
      };
      labels.forEach((label, i) => {
        const dot = document.createElement('button'); dot.type = 'button'; dot.className = 'art-dot';
        dot.setAttribute('aria-label', label);
        dot.addEventListener('click', () => show(i)); dots.append(dot);
      });
      host.querySelectorAll('[data-art-step]').forEach((button) => button.addEventListener('click', () => {
        show(index + Number(button.dataset.artStep));
      }));
      controls.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home'].includes(event.key)) return;
        event.preventDefault(); show(event.key === 'Home' ? 0 : index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      show(0, false); controls.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => { if (host.isConnected) host.dataset.stackReady = ''; }));
    } catch { /* Keep the server-rendered composition and native product links available. */ }
  };
  const initPool = (host) => {
    const status = host.querySelector('[data-pool-status]');
    const more = host.querySelector('.pool-load-more');
    const dialog = host.querySelector('.checklist-dialog');
    let button = host.querySelector('[data-pool-more]');
    if (!button) {
      button = document.createElement('button'); button.type = 'button';
      button.className = 'button-secondary'; button.dataset.poolMore = '';
      more.prepend(button);
    }
    button.hidden = true;
    let loaded = false;
    const node = (tag, className, text) => {
      const element = document.createElement(tag); element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    };
    const load = async () => {
      button.disabled = true; host.setAttribute('aria-busy', 'true');
      status.classList.remove('visually-hidden'); status.textContent = 'Loading the current card lineup…';
      try {
        const cards = await loadCards(host.dataset.packUrl, 'value', true);
        if (!host.isConnected) return;
        const count = cards.length;
        const availableCount = cards.filter(card => card.status === 'available').length;
        const percent = availableCount ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(100 / availableCount) : '';
        host.querySelectorAll('[data-pull-odds]').forEach(label => {
          label.textContent = availableCount ? `${percent}% per remaining card (1 in ${availableCount}). All ${availableCount} remaining cards have equal odds. Cards already assigned cannot be drawn again.` : 'No cards remain to pull. This gallery preserves the batch history.';
        });
        host.querySelectorAll('[data-pool-tier-group], [data-checklist-tier]').forEach(group => group.remove());
        const group = node('section', 'pool-tier'); group.dataset.poolTierGroup = 'lineup';
        const grid = node('div', 'pool-grid');
        group.append(node('h3', 'pool-tier-heading', 'Cards in this batch'), grid);
        const listGroup = node('section', 'checklist-tier'); listGroup.dataset.checklistTier = 'lineup';
        const list = node('ul', 'checklist-rows');
        listGroup.append(node('h3', '', 'Cards in this batch'), list);
        cards.forEach((card, index) => {
          const article = node('article', 'pool-card'); article.dataset.poolCardId = card.id;
          article.dataset.poolTier = 'lineup'; article.dataset.cardStatus = card.status; article.tabIndex = -1;
          if (index >= 24) article.dataset.poolOverflow = '';
          const holder = node('div', 'pool-card-image'); const image = new Image();
          image.src = card.image; image.alt = ''; image.width = 600; image.height = 900; image.loading = 'lazy';
          holder.append(image); article.append(holder, node('h3', '', card.name), node('p', '', card.detail));
          const value = card.price > 0 ? `Est. ${formatMoney(card.price, card.currency)}` : '';
          if (value) article.append(node('p', 'pool-card-value', value));
          grid.append(article);
          const row = document.createElement('li'); row.dataset.checklistCardId = card.id;
          const name = node('span', 'checklist-name', card.name); name.title = card.title;
          row.append(name, node('span', 'checklist-description', card.detail), node('span', 'checklist-rate', value));
          list.append(row);
        });
        more.before(group); dialog.append(listGroup);
        loaded = true; button.hidden = count <= 24; button.textContent = 'Load more';
        status.textContent = count ? `${count} cards in this batch.` : 'No cards are listed for this batch.';
        status.classList.remove('visually-hidden');
      } catch {
        status.textContent = 'The card lineup could not be loaded. Please try again.';
        status.classList.remove('visually-hidden'); button.hidden = false; button.textContent = 'Try again';
      } finally { button.disabled = false; host.removeAttribute('aria-busy'); }
    };
    button.addEventListener('click', () => {
      if (!loaded) { load(); return; }
      const queued = [...host.querySelectorAll('.pool-card[data-pool-overflow]')];
      const revealed = queued.slice(0, 24);
      revealed.forEach(card => card.removeAttribute('data-pool-overflow'));
      status.textContent = `${host.querySelectorAll('.pool-card:not([data-pool-overflow])').length} cards shown.`;
      if (queued.length <= 24) { button.hidden = true; revealed[0]?.focus({ preventScroll: true }); }
    });
    load();
  };
  const initialize = (root) => root.querySelectorAll('[data-pack-preview], [data-pack-art], [data-pack-pool], [data-hero-cards]').forEach((host) => {
    if (initialized.has(host)) return;
    initialized.add(host);
    if (host.hasAttribute('data-hero-cards')) initHero(host);
    else if (host.hasAttribute('data-pack-art')) initArt(host);
    else if (host.hasAttribute('data-pack-pool')) initPool(host);
    else initPreview(host);
  });
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('piranha:product:updated', (event) => initialize(event.target));
})();
